import React from 'react';
import {
  Badge, Button, Drawer, DrawerBody, DrawerHeader, SkeletonRow, SkeletonText, useToast,
} from '@parsina/framework';
import { FileSpreadsheet } from 'lucide-react';

import { useTranslation, LTR_TEXT } from '../../i18n';
import { useShortcut } from '../../lib/shortcuts';
import { EMPTY, formatUsd } from '../../lib/format';
import { num, parsePriceSource, toCsv } from '../../lib/aiPricingView';

/**
 * The cells, badges and small chrome every view of the pricing screen shares.
 *
 * They exist so a NULL renders as «—» in one place, a dollar figure gets its
 * four decimals in one place, and a margin's tone is decided by one rule —
 * three things that drift the moment each table hand-rolls them.
 */

// ── labels ──────────────────────────────────────────────────────────────────

/**
 * How the reader's language names a service. The BFF ships both labels; the
 * locale descriptor says which one the reader reads (`readsPersianData`), so
 * no render site branches on a locale id.
 */
export function useServiceLabel() {
  const { readsPersianData } = useTranslation();
  return React.useCallback(
    (row) => (readsPersianData ? row?.labelFa : row?.labelEn) || row?.labelFa || row?.labelEn || row?.serviceKey || EMPTY,
    [readsPersianData],
  );
}

/** A catalogue label for an enum-ish value, or the raw value when unknown. */
export function useEnumLabel(prefix) {
  const { t, has } = useTranslation();
  return React.useCallback((value) => {
    if (value == null || value === '') return EMPTY;
    const key = `${prefix}.${value}`;
    return has && has(key) ? t(key) : String(value);
  }, [t, has, prefix]);
}

// ── cells ───────────────────────────────────────────────────────────────────

/** A machine identifier: LTR island, monospace, never localised. */
export function Code({ children, className = '', title }) {
  if (children == null || children === '') return <span>{EMPTY}</span>;
  return (
    <span {...LTR_TEXT} className={`font-mono text-[12px] ${className}`} title={title}>
      {children}
    </span>
  );
}

/** A USD figure to four decimals in the reader's digits; NULL → «—». */
export function UsdCell({ value, title, className = '' }) {
  const { t, formatNumber } = useTranslation();
  return (
    <span className={`tabular-nums ${className}`} title={title}>
      {formatUsd(value, { formatNumber, t })}
    </span>
  );
}

/** A Parseh figure (up to 2 decimals); the column header carries the unit. */
export function ParsehCell({ value, suffix, className = '' }) {
  const { formatNumber } = useTranslation();
  const n = num(value);
  return (
    <span className={`tabular-nums ${className}`}>
      {n == null ? EMPTY : formatNumber(n, { maximumFractionDigits: 2 })}
      {n != null && suffix ? suffix : null}
    </span>
  );
}

/**
 * A TOMAN figure, already converted by the BFF. Rendered through
 * `formatNumber` on purpose — `formatMoney` takes RIALS and divides by ten,
 * which would show a tenth of the cost (design §7.5).
 */
export function TomanCell({ value, className = '' }) {
  const { formatNumber } = useTranslation();
  const n = num(value);
  return (
    <span className={`tabular-nums ${className}`}>
      {n == null ? EMPTY : formatNumber(n, { maximumFractionDigits: 0 })}
    </span>
  );
}

/** A plain count in the reader's digits; NULL → «—». */
export function CountCell({ value, options, className = '' }) {
  const { formatNumber } = useTranslation();
  const n = num(value);
  return (
    <span className={`tabular-nums ${className}`}>
      {n == null ? EMPTY : formatNumber(n, options)}
    </span>
  );
}

/** A percentage the BFF computed («۸۵٪»); NULL → «—». */
export function PercentCell({ value, className = '' }) {
  const { formatPercent } = useTranslation();
  const n = num(value);
  return <span className={`tabular-nums ${className}`}>{n == null ? EMPTY : formatPercent(n)}</span>;
}

// ── badges ──────────────────────────────────────────────────────────────────

/**
 * A margin, toned by sign. Negative margins are the whole point of the page
 * (design §8.3), so they carry the danger chip; a thin one warns; NULL is a
 * plain dash, never a zero and never a colour.
 */
export function MarginBadge({ value, 'data-testid': testId }) {
  const { formatPercent } = useTranslation();
  const n = num(value);
  if (n == null) return <span data-testid={testId}>{EMPTY}</span>;
  const variant = n < 0 ? 'destructive' : n < 30 ? 'warning' : 'success';
  return (
    <Badge variant={variant} className="tabular-nums" data-testid={testId} data-tone={variant}>
      {formatPercent(n)}
    </Badge>
  );
}

/** `policy:<id>` / `code_default` / `override` as words. */
export function PriceSourceBadge({ source }) {
  const { t, formatDigits } = useTranslation();
  const { kind, id } = parsePriceSource(source);
  if (kind === 'policy') {
    return <Badge variant="outline">{t('aiPricing.services.source.policy', { id: formatDigits(id) })}</Badge>;
  }
  if (kind === 'override') return <Badge variant="default">{t('aiPricing.services.source.override')}</Badge>;
  return <Badge variant="warning">{t('aiPricing.services.source.code_default')}</Badge>;
}

// ── CSV ─────────────────────────────────────────────────────────────────────

/**
 * Put text on the clipboard. The viewer sandbox blocks downloads, so the
 * export is a copy; `execCommand` is the fallback for a browser (or a jsdom)
 * with no async clipboard.
 */
export async function copyText(text) {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through to the legacy path */ }
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.className = 'sr-only';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand ? document.execCommand('copy') : false;
    area.remove();
    return !!ok;
  } catch {
    return false;
  }
}

/**
 * «خروجی CSV» — Latin digits and ISO dates whatever the locale (F15). The
 * columns' `value` functions return RAW wire values; the on-screen formatting
 * must not leak into a file that is going into a spreadsheet.
 */
export function CsvExportButton({ columns, rows, disabled, 'data-testid': testId }) {
  const { t } = useTranslation();
  const toast = useToast();
  const onClick = async () => {
    const ok = await copyText(toCsv(columns, rows));
    if (ok) toast.success(t('aiPricing.csv.copied'));
    else toast.error(t('aiPricing.csv.failed'));
  };
  return (
    <Button variant="ghost" size="sm" onClick={onClick} disabled={disabled || !rows?.length} data-testid={testId}>
      <FileSpreadsheet size={14} aria-hidden />
      {t('aiPricing.csv.export')}
    </Button>
  );
}

// ── drawer ──────────────────────────────────────────────────────────────────

/**
 * The kit `Drawer` with the panel's dialog semantics on top: a named
 * `role="dialog"`, `Esc` through the shortcut registry (so the `?` overlay
 * lists it and a stacked dialog can shadow it), and a test hook.
 */
export function DetailDrawer({ open, onClose, title, sub, width = 640, children, 'data-testid': testId }) {
  const { t } = useTranslation();
  useShortcut('esc', onClose, {
    label: t('shortcuts.action.closeDrawer'),
    group: t('shortcuts.groups.aiPricing'),
    enabled: open,
  });
  return (
    <Drawer open={open} onClose={onClose} width={width}>
      <div role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined} className="flex h-full min-h-0 flex-col" data-testid={testId}>
        <DrawerHeader title={title} sub={sub} onClose={onClose} />
        <DrawerBody>{children}</DrawerBody>
      </div>
    </Drawer>
  );
}

// ── skeletons ───────────────────────────────────────────────────────────────

/** A table's loading body: rows in the real frame so nothing moves on arrival. */
export function TableSkeleton({ cols = 8, rows = 8, label, 'data-testid': testId }) {
  return (
    <div data-testid={testId} data-state="loading" role="status" aria-busy="true" aria-label={label}>
      <table className="w-full">
        <tbody>
          {Array.from({ length: rows }).map((_, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <SkeletonRow key={i} cols={cols} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A card-shaped placeholder for a definition list or a chart. */
export function BlockSkeleton({ lines = 4, height = 14 }) {
  return (
    <div className="flex flex-col gap-3" aria-hidden>
      {Array.from({ length: lines }).map((_, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <SkeletonText key={i} width={`${90 - (i % 3) * 20}%`} height={height} />
      ))}
    </div>
  );
}

/** One labelled fact in a drawer's definition list. */
export function Fact({ label, value, ltr }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="truncate font-semibold text-foreground" {...(ltr ? LTR_TEXT : {})}>
        {value == null || value === '' ? EMPTY : value}
      </dd>
    </div>
  );
}
